import { memo, useCallback } from "react"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import type { ViewItem } from "@/features/views/hooks/useViewData"
import { TaskItem } from "@/features/tasks/components/TaskItem"
import { useViewContext } from "@/app/stores/viewContext"
import type { SwipeAction } from "@/features/tasks/components/SwipeableTaskRow"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SortableTaskRowProps {
  item: ViewItem
  /** Absolute pixel offset from virtualizer (undefined = non-virtualized) */
  virtualStart?: number
  /** Pixel height from virtualizer (undefined = non-virtualized) */
  virtualSize?: number
  /** scrollMargin offset (only for virtualized rows) */
  scrollMargin?: number
  /**
   * Virtualizer measurement callback — enables dynamic height observation so
   * the virtualizer can reposition items when drag sources collapse via CSS.
   * Pass `virtualizer.measureElement` here.
   */
  measureRef?: (el: HTMLElement | null) => void
  /** Virtual item index (data-index attribute required by measureElement) */
  virtualIndex?: number
  onComplete: (id: string) => void
  onUncomplete: (id: string) => void
  onSwipeAction?: (id: string, action: SwipeAction) => void
  /** Whether this task is currently expanded (active detail) */
  isActive?: boolean
  /** Whether this task was just closed (triggers close animation on remount) */
  isClosing?: boolean
  /** Whether to auto-focus title in detail mode */
  editTitle?: boolean
  /** Open task detail */
  onOpen?: (id: string) => void
  /** Close task detail */
  onClose?: () => void
  /** First visible item in a contiguous visual group (rounded top corners) */
  isFirstInGroup?: boolean
  /** Last visible item in a contiguous visual group (rounded bottom corners) */
  isLastInGroup?: boolean
}

// ---------------------------------------------------------------------------
// Custom memo comparator — only re-render when task data actually changes.
// Prevents re-renders from new object references after Dexie liveQuery fires.
// ---------------------------------------------------------------------------

function arePropsEqual(
  prev: SortableTaskRowProps,
  next: SortableTaskRowProps,
): boolean {
  const pt = prev.item.task
  const nt = next.item.task
  return (
    pt.id === nt.id &&
    pt.updatedAt === nt.updatedAt &&
    pt.status === nt.status &&
    pt.docHtml === nt.docHtml &&
    pt.recurringTemplateId === nt.recurringTemplateId &&
    pt.note === nt.note &&
    pt.title === nt.title &&
    pt.dueAt === nt.dueAt &&
    pt.tags === nt.tags &&
    prev.item.sortIndex === next.item.sortIndex &&
    prev.virtualStart === next.virtualStart &&
    prev.virtualSize === next.virtualSize &&
    prev.scrollMargin === next.scrollMargin &&
    prev.measureRef === next.measureRef &&
    prev.virtualIndex === next.virtualIndex &&
    prev.onComplete === next.onComplete &&
    prev.onUncomplete === next.onUncomplete &&
    prev.onSwipeAction === next.onSwipeAction &&
    prev.isActive === next.isActive &&
    prev.isClosing === next.isClosing &&
    prev.editTitle === next.editTitle &&
    prev.onOpen === next.onOpen &&
    prev.onClose === next.onClose &&
    prev.isFirstInGroup === next.isFirstInGroup &&
    prev.isLastInGroup === next.isLastInGroup
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * SortableTaskRow — wraps TaskItem with @dnd-kit sortable behavior.
 *
 * Works in two modes:
 * - **Virtualized** (virtualStart + virtualSize provided): absolute positioning
 * - **Non-virtualized** (no virtual props): normal flow layout
 *
 * Extracted from DndTestPage.SortableRow to be reusable across all views.
 */
export const SortableTaskRow = memo(function SortableTaskRow({
  item,
  virtualStart,
  virtualSize,
  scrollMargin = 0,
  measureRef,
  virtualIndex,
  onComplete,
  onUncomplete,
  onSwipeAction,
  isActive,
  isClosing,
  editTitle,
  onOpen,
  onClose,
  isFirstInGroup,
  isLastInGroup,
}: SortableTaskRowProps) {
  const task = item.task
  const id = task.id ?? "no-id"
  const {
    setNodeRef,
    setActivatorNodeRef,
    listeners,
    isDragging,
    transform,
    transition,
  } = useSortable({ id, disabled: isActive })

  // Multi-drag companion detection: item is in dragCompanionIds while a drag is active.
  // Uses dragCompanionIds (set by DndListProvider) instead of selectedTaskIds so that
  // section children are dimmed even when not explicitly selected.
  const isMultiDragCompanion = useViewContext(
    useCallback(
      (s: {
        isDragging: boolean
        dragCompanionIds: string[]
      }) =>
        s.isDragging &&
        s.dragCompanionIds.includes(id) &&
        !isDragging,
      [id, isDragging],
    ),
  )

  const handleSwipe = useCallback(
    (taskId: string, action: SwipeAction) => {
      onSwipeAction?.(taskId, action)
    },
    [onSwipeAction],
  )

  const isVirtualized = virtualStart != null

  // Merge useSortable's setNodeRef with the virtualizer's measureElement ref.
  // When measureRef is provided, the virtualizer uses ResizeObserver to track
  // actual DOM height. This lets it detect CSS grid-collapse (0fr) on drag
  // sources and reposition subsequent items to close the gap.
  const combinedRef = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node)
      if (measureRef) measureRef(node)
    },
    [setNodeRef, measureRef],
  )

  // Active/dragging rows need position + z-index for stacking.
  // Non-active rows: no position → stay below the .task-list-dimmed ::before (z-30).
  // Active row z-50 is also set by .task-row-active CSS on TaskItem,
  // but repeated here for virtualized absolute-positioned wrappers.
  //
  // touch-action is handled by the drag handle activator (title area),
  // NOT on the wrapper — this allows normal scrolling on the row body.
  //
  // All rows — including drag sources — keep their useSortable transform
  // and transition.  This is essential for the placeholder animation:
  // the drag source's transform IS what moves it to the drop position,
  // creating the visual gap/preview.  When hovering the sidebar, CSS
  // !important overrides (body.dnd-over-sidebar) collapse height and
  // remove transforms so drag sources disappear from the list.

  const style: React.CSSProperties = isVirtualized
    ? {
        position: "absolute",
        top: virtualStart - scrollMargin,
        left: 0,
        width: "100%",
        height: isActive ? undefined : virtualSize,
        transform: CSS.Translate.toString(transform),
        transition,
        zIndex: isActive ? 50 : isDragging ? 0 : undefined,
      }
    : {
        transform: CSS.Translate.toString(transform),
        transition,
        ...(isActive && { position: "relative" as const, zIndex: 50 }),
        ...(isDragging && {
          position: "relative" as const,
          zIndex: 0,
        }),
        ...(isMultiDragCompanion && {
          position: "relative" as const,
          zIndex: 0,
        }),
      }

  return (
    <div
      ref={combinedRef}
      style={style}
      data-task-id={id}
      {...(virtualIndex != null && { "data-index": virtualIndex })}
      {...(isDragging && { "data-drag-source": "" })}
      {...(isMultiDragCompanion && { "data-drag-companion": "" })}
    >
      <TaskItem
        task={task}
        disabled={isDragging || isMultiDragCompanion}
        onComplete={onComplete}
        onUncomplete={onUncomplete}
        onSwipeAction={handleSwipe}
        isActive={isActive}
        isClosing={isClosing}
        startAtData={item.startAtData}
        editTitle={editTitle}
        onOpen={onOpen}
        onClose={onClose}
        dragHandleRef={setActivatorNodeRef}
        dragListeners={listeners}
        isFirstInGroup={isFirstInGroup}
        isLastInGroup={isLastInGroup}
      />
    </div>
  )
}, arePropsEqual)
